import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { apiClient } from "@/lib/api";
import { BookingWindowStatus } from "@/types/booking-window";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "react-router-dom";

const bookingWindowSchema = z.object({
  name: z.string().min(3, "Name must be at least 3 characters").max(100, "Name cannot exceed 100 characters"),
  slug: z.string().min(3, "Slug must be at least 3 characters").max(50, "Slug cannot exceed 50 characters").regex(/^[a-z0-9-]+$/, "Slug must contain only lowercase letters, numbers, and hyphens"),
  status: z.nativeEnum(BookingWindowStatus),
  submission_start_date: z.string().min(1, "Submission start date is required"),
  submission_end_date: z.string().min(1, "Submission end date is required"),
  packaging_start_date: z.string().min(1, "Packaging start date is required"),
  packaging_end_date: z.string().min(1, "Packaging end date is required"),
  presentation_date: z.string().min(1, "Presentation date is required"),
}).refine((data) => {
  // Check if submission start date is not in the past
  if (data.submission_start_date) {
    const now = new Date();
    const submissionStart = new Date(data.submission_start_date);
    return submissionStart >= now;
  }
  return true;
}, {
  message: "Submission start date cannot be in the past",
  path: ["submission_start_date"],
}).refine((data) => {
  if (data.submission_start_date && data.submission_end_date) {
    return new Date(data.submission_end_date) > new Date(data.submission_start_date);
  }
  return true;
}, {
  message: "Submission end date must be after start date",
  path: ["submission_end_date"],
}).refine((data) => {
  if (data.packaging_start_date && data.packaging_end_date) {
    return new Date(data.packaging_end_date) > new Date(data.packaging_start_date);
  }
  return true;
}, {
  message: "Packaging end date must be after start date",
  path: ["packaging_end_date"],
}).refine((data) => {
  if (data.submission_end_date && data.packaging_start_date) {
    return new Date(data.packaging_start_date) >= new Date(data.submission_end_date);
  }
  return true;
}, {
  message: "Packaging must start on or after submission ends",
  path: ["packaging_start_date"],
}).refine((data) => {
  if (data.packaging_end_date && data.presentation_date) {
    return new Date(data.presentation_date) >= new Date(data.packaging_end_date);
  }
  return true;
}, {
  message: "Presentation must be on or after packaging ends",
  path: ["presentation_date"],
});

type BookingWindowFormValues = z.infer<typeof bookingWindowSchema>;

export default function CreateBookingWindowPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Get current date in the user's local timezone for datetime-local input
  // datetime-local inputs work in the user's local timezone, so we use local time
  const getCurrentDateTime = () => {
    const now = new Date();
    // Get local date/time components (this automatically handles user's timezone)
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const minDateTime = getCurrentDateTime();

  const form = useForm<BookingWindowFormValues>({
    resolver: zodResolver(bookingWindowSchema),
    defaultValues: {
      name: "",
      slug: "",
      status: BookingWindowStatus.DRAFT,
      submission_start_date: "",
      submission_end_date: "",
      packaging_start_date: "",
      packaging_end_date: "",
      presentation_date: "",
    },
  });

  // Helper function to convert local datetime to UTC for backend storage
  const convertToUTC = (localDateTimeString: string) => {
    if (!localDateTimeString) return "";
    
    // CRITICAL: datetime-local input gives us a string like "2026-03-23T04:05"
    // This represents the LOCAL time in the user's timezone
    // We need to interpret this as local time and convert to UTC
    
    // Parse the datetime string components
    const [datePart, timePart] = localDateTimeString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    
    // Create a date object using the local timezone interpretation
    // This ensures the date is created in the user's current timezone
    const localDate = new Date(year, month - 1, day, hours, minutes);
    
    // Convert to UTC ISO string
    const utcString = localDate.toISOString();
    
    return utcString;
  };

  async function onSubmit(data: BookingWindowFormValues) {
    setIsSubmitting(true);
    try {
      console.log("🔍 DEBUG - CREATE FLOW - Original form data:", data);
      
      // Convert all datetime fields to UTC before sending to backend
      const utcData = {
        ...data,
        submission_start_date: convertToUTC(data.submission_start_date),
        submission_end_date: convertToUTC(data.submission_end_date),
        packaging_start_date: convertToUTC(data.packaging_start_date),
        packaging_end_date: convertToUTC(data.packaging_end_date),
        presentation_date: convertToUTC(data.presentation_date),
      };
      
      console.log("🔍 DEBUG - CREATE FLOW - UTC converted data:", utcData);
      
      await apiClient.createBookingWindow(utcData as unknown as import("@/types/booking-window").BookingWindowCreate);
      
      toast({
        title: "Success",
        description: "Booking window created successfully",
      });
      
      navigate("/admin/booking-windows");
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create booking window",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="container mx-auto py-10 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin/booking-windows">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Create Booking Window</h1>
          <p className="text-muted-foreground">
            Set up a new booking window cycle.
          </p>
        </div>
      </div>

      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>Window Details</CardTitle>
          <CardDescription>
            Configure the timeline and status for this booking window.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground">
                      Window Name <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="March 2026 Booking Window" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground">
                      Slug <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="march-2026-window" {...field} />
                    </FormControl>
                    <FormDescription>URL-friendly identifier (lowercase, hyphens)</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-medium text-sm text-muted-foreground">Submission Period</h3>
                  <FormField
                    control={form.control}
                    name="submission_start_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground">
                          Start Date <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="datetime-local"
                            {...field}
                            min={minDateTime}
                            max="2030-12-31T23:59"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="submission_end_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground">
                          End Date <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="datetime-local"
                            {...field}
                            min={minDateTime}
                            max="2030-12-31T23:59"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-4">
                  <h3 className="font-medium text-sm text-muted-foreground">Packaging Period</h3>
                  <FormField
                    control={form.control}
                    name="packaging_start_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground">
                          Start Date <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="datetime-local"
                            {...field}
                            min={minDateTime}
                            max="2030-12-31T23:59"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="packaging_end_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground">
                          End Date <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="datetime-local"
                            {...field}
                            min={minDateTime}
                            max="2030-12-31T23:59"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <FormField
                  control={form.control}
                  name="presentation_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground">
                        Presentation Date <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="datetime-local"
                          {...field}
                          min={minDateTime}
                          max="2030-12-31T23:59"
                        />
                      </FormControl>
                      <FormDescription>Date for presentation to brands</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground">
                        Status <span className="text-red-500">*</span>
                      </FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={BookingWindowStatus.DRAFT}>Draft</SelectItem>
                          <SelectItem value={BookingWindowStatus.OPEN}>Open</SelectItem>
                          <SelectItem value={BookingWindowStatus.PACKAGING}>Packaging</SelectItem>
                          <SelectItem value={BookingWindowStatus.PRESENTATION}>Presentation</SelectItem>
                          <SelectItem value={BookingWindowStatus.CLOSED}>Closed</SelectItem>
                          <SelectItem value={BookingWindowStatus.ARCHIVED}>Archived</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex justify-end gap-4 pt-4">
                <Button variant="outline" type="button" onClick={() => navigate("/admin/booking-windows")}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create Booking Window"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
